import { useCallback, useEffect, useRef } from "react";
import {
  useVideoUI,
  useVideoUIDispatch,
  SET_CONTENT_SHARE_ZOOM,
  RESET_CONTENT_SHARE_ZOOM,
} from "../VideoUIContext";

export const useContentShareZoom = () => {
  const { contentShareZoom } = useVideoUI();

  const dispatch = useVideoUIDispatch();

  const containerRef = useRef<HTMLDivElement>(null);

  const isDragging = useRef(false);

  const lastTouchDistance = useRef(0);

  const lastTouchCenter = useRef({ x: 0, y: 0 });

  const lastTouchPos = useRef<{ x: number; y: number } | null>(null);

  const setZoom = useCallback(
    (scale: number, translateX: number, translateY: number) => {
      dispatch({
        type: SET_CONTENT_SHARE_ZOOM,
        payload: { scale, translateX, translateY },
      });
    },
    [dispatch]
  );

  const resetZoom = useCallback(() => {
    dispatch({ type: RESET_CONTENT_SHARE_ZOOM });
  }, [dispatch]);

  // Calculate bounds and clamp translation so video edges never leave the container
  const getVideoAndContainerSizes = () => {
    if (!containerRef.current) return null;

    const container = containerRef.current;

    const containerRect = container.getBoundingClientRect();

    const videoContainer = container.querySelector(
      ".aspect-video"
    ) as HTMLElement;

    if (!videoContainer) return null;

    const videoElement = videoContainer.querySelector(
      "video"
    ) as HTMLVideoElement;

    if (!videoElement) return null;

    const videoWidth = videoElement.videoWidth || videoElement.offsetWidth;

    const videoHeight = videoElement.videoHeight || videoElement.offsetHeight;

    return {
      containerWidth: containerRect.width,
      containerHeight: containerRect.height,
      videoWidth,
      videoHeight,
    };
  };

  const calculateBounds = useCallback((scale: number) => {
    const sizes = getVideoAndContainerSizes();

    if (!sizes) return { minScale: 1, maxPanX: 0, maxPanY: 0 };

    const { containerWidth, containerHeight, videoWidth, videoHeight } = sizes;

    const minScale = Math.max(
      containerWidth / videoWidth,
      containerHeight / videoHeight
    );

    const scaledWidth = videoWidth * scale;

    const scaledHeight = videoHeight * scale;

    const maxPanX = Math.max(0, (scaledWidth - containerWidth) / 2);

    const maxPanY = Math.max(0, (scaledHeight - containerHeight) / 2);

    return { minScale, maxPanX, maxPanY };
  }, []);

  // Clamp translation so video edges never leave the container
  const constrainTranslation = useCallback(
    (scale: number, translateX: number, translateY: number) => {
      if (!containerRef.current) return { scale, translateX: 0, translateY: 0 };

      const container = containerRef.current;

      const rect = container.getBoundingClientRect();

      const containerW = rect.width;

      const containerH = rect.height;

      // Get the video element's natural size
      const videoEl = container.querySelector("video");

      let videoW = containerW,
        videoH = containerH;

      if (videoEl && videoEl.videoWidth && videoEl.videoHeight) {
        videoW = videoEl.videoWidth;
        videoH = videoEl.videoHeight;
      }

      // Calculate fit rectangle (objectFit='contain')
      const containerRatio = containerW / containerH;

      const videoRatio = videoW / videoH;

      let fitW, fitH;

      if (videoRatio > containerRatio) {
        fitW = containerW;
        fitH = containerW / videoRatio;
      } else {
        fitH = containerH;
        fitW = containerH * videoRatio;
      }

      // Now, the visible area is fitW x fitH, centered in the container
      const minScale = 1; // Don't allow zooming out beyond initial view

      const constrainedScale = Math.max(minScale, Math.min(3, scale));

      if (constrainedScale <= 1) {
        return { scale: constrainedScale, translateX: 0, translateY: 0 };
      }

      // The max pan is based on the fit rectangle
      const maxPanX = (fitW / 2) * (1 - 1 / constrainedScale);

      const maxPanY = (fitH / 2) * (1 - 1 / constrainedScale);

      const constrainedTranslateX = Math.max(
        -maxPanX,
        Math.min(maxPanX, translateX)
      );

      const constrainedTranslateY = Math.max(
        -maxPanY,
        Math.min(maxPanY, translateY)
      );

      return {
        scale: constrainedScale,
        translateX: constrainedTranslateX,
        translateY: constrainedTranslateY,
      };
    },
    []
  );

  // Get the minimum scale - now always 1 (initial view)
  const getMinScale = useCallback(() => {
    return 1; // Don't allow zooming out beyond initial view
  }, []);

  // Simple zoom function that respects minimum scale
  const zoomToScale = useCallback(
    (targetScale: number) => {
      const minScale = getMinScale(); // Always 1

      const constrainedScale = Math.max(minScale, Math.min(3, targetScale));

      // Reset translation when zooming out to minimum scale
      const { maxPanX, maxPanY } = calculateBounds(constrainedScale);

      const newTranslateX = maxPanX === 0 ? 0 : contentShareZoom.translateX;

      const newTranslateY = maxPanY === 0 ? 0 : contentShareZoom.translateY;

      setZoom(constrainedScale, newTranslateX, newTranslateY);
    },
    [
      getMinScale,
      calculateBounds,
      contentShareZoom.translateX,
      contentShareZoom.translateY,
      setZoom,
    ]
  );

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) return;

      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();

        const rect = containerRef.current.getBoundingClientRect();

        const cx = rect.left + rect.width / 2;

        const cy = rect.top + rect.height / 2;

        const mx = e.clientX;

        const my = e.clientY;

        const dx = mx - cx;

        const dy = my - cy;

        const oldScale = contentShareZoom.scale;

        const delta = e.deltaY > 0 ? 0.9 : 1.1;

        const newScale = Math.max(1, Math.min(3, oldScale * delta)); // Min scale is 1

        // Adjust pan so the point under the cursor stays fixed
        const newTranslateX =
          (contentShareZoom.translateX - dx) * (newScale / oldScale) + dx;

        const newTranslateY =
          (contentShareZoom.translateY - dy) * (newScale / oldScale) + dy;

        const constrained = constrainTranslation(
          newScale,
          newTranslateX,
          newTranslateY
        );

        setZoom(
          constrained.scale,
          constrained.translateX,
          constrained.translateY
        );
      }
    },
    [contentShareZoom, setZoom, constrainTranslation]
  );

  const getTouchDistance = useCallback((touches: TouchList) => {
    if (touches.length < 2) return 0;

    const dx = touches[0].clientX - touches[1].clientX;

    const dy = touches[0].clientY - touches[1].clientY;

    return Math.sqrt(dx * dx + dy * dy);
  }, []);

  const getTouchCenter = useCallback((touches: TouchList) => {
    if (touches.length < 2) return { x: 0, y: 0 };

    const x = (touches[0].clientX + touches[1].clientX) / 2;

    const y = (touches[0].clientY + touches[1].clientY) / 2;

    return { x, y };
  }, []);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (e.touches.length === 1 && contentShareZoom.isZoomed) {
        // start drag
        lastTouchPos.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
        };
      } else if (e.touches.length === 2) {
        lastTouchDistance.current = getTouchDistance(e.touches);

        lastTouchCenter.current = getTouchCenter(e.touches);
      }
    },
    [contentShareZoom.isZoomed, getTouchDistance, getTouchCenter]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (
        e.touches.length === 1 &&
        contentShareZoom.isZoomed &&
        lastTouchPos.current
      ) {
        e.preventDefault();

        const dx = e.touches[0].clientX - lastTouchPos.current.x;

        const dy = e.touches[0].clientY - lastTouchPos.current.y;

        lastTouchPos.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
        };

        const newTranslateX =
          contentShareZoom.translateX + dx / contentShareZoom.scale;

        const newTranslateY =
          contentShareZoom.translateY + dy / contentShareZoom.scale;

        const constrained = constrainTranslation(
          contentShareZoom.scale,
          newTranslateX,
          newTranslateY
        );

        setZoom(
          constrained.scale,
          constrained.translateX,
          constrained.translateY
        );
      } else if (e.touches.length === 2) {
        e.preventDefault();

        const rect = containerRef.current!.getBoundingClientRect();

        const cx = rect.left + rect.width / 2;

        const cy = rect.top + rect.height / 2;

        const currentDistance = getTouchDistance(e.touches);

        const currentCenter = getTouchCenter(e.touches);

        if (lastTouchDistance.current > 0) {
          const oldScale = contentShareZoom.scale;

          const scale = currentDistance / lastTouchDistance.current;

          const newScale = Math.max(1, Math.min(3, oldScale * scale)); // Min scale is 1

          const dx = currentCenter.x - cx;

          const dy = currentCenter.y - cy;

          // Adjust pan so the point under the pinch center stays fixed
          const newTranslateX =
            (contentShareZoom.translateX - dx) * (newScale / oldScale) + dx;

          const newTranslateY =
            (contentShareZoom.translateY - dy) * (newScale / oldScale) + dy;

          const constrained = constrainTranslation(
            newScale,
            newTranslateX,
            newTranslateY
          );

          setZoom(
            constrained.scale,
            constrained.translateX,
            constrained.translateY
          );
        }

        lastTouchDistance.current = currentDistance;

        lastTouchCenter.current = currentCenter;
      }
    },
    [
      contentShareZoom,
      setZoom,
      getTouchDistance,
      getTouchCenter,
      constrainTranslation,
    ]
  );

  const handleTouchEnd = useCallback(() => {
    lastTouchPos.current = null;
    lastTouchDistance.current = 0;
  }, []);

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (contentShareZoom.isZoomed) {
        isDragging.current = true;

        e.preventDefault();
      }
    },
    [contentShareZoom.isZoomed]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging.current && contentShareZoom.isZoomed) {
        const deltaX = e.movementX / contentShareZoom.scale;

        const deltaY = e.movementY / contentShareZoom.scale;

        const newTranslateX = contentShareZoom.translateX + deltaX;

        const newTranslateY = contentShareZoom.translateY + deltaY;

        // Apply bounds constraints
        const constrained = constrainTranslation(
          contentShareZoom.scale,
          newTranslateX,
          newTranslateY
        );

        setZoom(
          constrained.scale,
          constrained.translateX,
          constrained.translateY
        );
      }
    },
    [contentShareZoom, setZoom, constrainTranslation]
  );

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    // Add event listeners
    container.addEventListener("wheel", handleWheel, { passive: false });

    container.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });

    container.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });

    container.addEventListener("touchend", handleTouchEnd);

    container.addEventListener("mousedown", handleMouseDown);

    document.addEventListener("mousemove", handleMouseMove);

    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      container.removeEventListener("wheel", handleWheel);
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
      container.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    handleWheel,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  ]);

  return {
    containerRef,
    contentShareZoom,
    setZoom,
    resetZoom,
    calculateBounds,
    getMinScale,
    zoomToScale,
  };
};
